import { Database, Info, Copy } from 'lucide-react';
import { StripeOperation } from '../../../types/stripe';

interface Step5OutputMappingProps {
	operation: StripeOperation;
}

export function Step5OutputMapping({ operation }: Step5OutputMappingProps) {
	// Get available output fields for the current operation
	const getAvailableOutputFields = () => {
		// Only show the actual fields that come from Stripe API responses
		// No fake "base fields" that don't exist
		const operationFields: Array<{ id: string; label: string; description: string; variablePath: string }> = [];
		
		switch (operation) {
			case 'create_payment_intent':
			case 'confirm_payment_intent':
				operationFields.push(
					{ id: 'id', label: 'Payment Intent ID', description: 'Stripe payment intent ID (e.g., pi_...)', variablePath: '{{stripe-3.id}}' },
					{ id: 'amount', label: 'Amount', description: 'Amount in cents', variablePath: '{{stripe-3.amount}}' },
					{ id: 'currency', label: 'Currency', description: 'Three-letter ISO currency code', variablePath: '{{stripe-3.currency}}' },
					{ id: 'status', label: 'Status', description: 'Current payment status', variablePath: '{{stripe-3.status}}' },
					{ id: 'client_secret', label: 'Client Secret', description: 'Client secret for confirmation', variablePath: '{{stripe-3.client_secret}}' },
					{ id: 'created', label: 'Created Timestamp', description: 'Unix timestamp when payment intent was created', variablePath: '{{stripe-3.created}}' },
					{ id: 'livemode', label: 'Live Mode', description: 'Whether this is a live or test payment intent', variablePath: '{{stripe-3.livemode}}' }
				);
				break;
			case 'create_customer':
				operationFields.push(
					{ id: 'id', label: 'Customer ID', description: 'Stripe customer ID (e.g., cus_...)', variablePath: '{{stripe-3.id}}' },
					{ id: 'name', label: 'Customer Name', description: 'Customer name', variablePath: '{{stripe-3.name}}' },
					{ id: 'email', label: 'Customer Email', description: 'Customer email address', variablePath: '{{stripe-3.email}}' },
					{ id: 'created', label: 'Created Timestamp', description: 'Unix timestamp when customer was created', variablePath: '{{stripe-3.created}}' },
					{ id: 'livemode', label: 'Live Mode', description: 'Whether this is a live or test customer', variablePath: '{{stripe-3.livemode}}' },
					{ id: 'metadata', label: 'Metadata', description: 'Custom metadata associated with the customer', variablePath: '{{stripe-3.metadata}}' }
				);
				break;
			case 'create_subscription':
				operationFields.push(
					{ id: 'id', label: 'Subscription ID', description: 'Stripe subscription ID (e.g., sub_...)', variablePath: '{{stripe-3.id}}' },
					{ id: 'customer', label: 'Customer ID', description: 'Stripe customer ID', variablePath: '{{stripe-3.customer}}' },
					{ id: 'status', label: 'Status', description: 'Subscription status', variablePath: '{{stripe-3.status}}' },
					{ id: 'current_period_start', label: 'Current Period Start', description: 'Billing period start timestamp', variablePath: '{{stripe-3.current_period_start}}' },
					{ id: 'current_period_end', label: 'Current Period End', description: 'Billing period end timestamp', variablePath: '{{stripe-3.current_period_end}}' },
					{ id: 'created', label: 'Created Timestamp', description: 'Unix timestamp when subscription was created', variablePath: '{{stripe-3.created}}' },
					{ id: 'livemode', label: 'Live Mode', description: 'Whether this is a live or test subscription', variablePath: '{{stripe-3.livemode}}' }
				);
				break;
			case 'cancel_subscription':
				operationFields.push(
					{ id: 'id', label: 'Subscription ID', description: 'Stripe subscription ID (e.g., sub_...)', variablePath: '{{stripe-3.id}}' },
					{ id: 'customer', label: 'Customer ID', description: 'Stripe customer ID', variablePath: '{{stripe-3.customer}}' },
					{ id: 'status', label: 'Status', description: 'Subscription status after cancellation', variablePath: '{{stripe-3.status}}' },
					{ id: 'canceled_at', label: 'Canceled At', description: 'Unix timestamp when subscription was canceled', variablePath: '{{stripe-3.canceled_at}}' },
					{ id: 'current_period_start', label: 'Current Period Start', description: 'Billing period start timestamp', variablePath: '{{stripe-3.current_period_start}}' },
					{ id: 'current_period_end', label: 'Current Period End', description: 'Billing period end timestamp', variablePath: '{{stripe-3.current_period_end}}' },
					{ id: 'created', label: 'Created Timestamp', description: 'Unix timestamp when subscription was created', variablePath: '{{stripe-3.created}}' },
					{ id: 'livemode', label: 'Live Mode', description: 'Whether this is a live or test subscription', variablePath: '{{stripe-3.livemode}}' }
				);
				break;
			case 'refund_payment':
				operationFields.push(
					{ id: 'id', label: 'Refund ID', description: 'Stripe refund ID (e.g., re_...)', variablePath: '{{stripe-3.id}}' },
					{ id: 'amount', label: 'Amount', description: 'Amount refunded in cents', variablePath: '{{stripe-3.amount}}' },
					{ id: 'currency', label: 'Currency', description: 'Three-letter ISO currency code', variablePath: '{{stripe-3.currency}}' },
					{ id: 'status', label: 'Status', description: 'Refund status', variablePath: '{{stripe-3.status}}' },
					{ id: 'payment_intent', label: 'Payment Intent ID', description: 'Associated payment intent ID', variablePath: '{{stripe-3.payment_intent}}' },
					{ id: 'created', label: 'Created Timestamp', description: 'Unix timestamp when refund was created', variablePath: '{{stripe-3.created}}' },
					{ id: 'livemode', label: 'Live Mode', description: 'Whether this is a live or test refund', variablePath: '{{stripe-3.livemode}}' }
				);
				break;
			case 'attach_payment_method':
				operationFields.push(
					{ id: 'id', label: 'Payment Method ID', description: 'Stripe payment method ID (e.g., pm_...)', variablePath: '{{stripe-3.id}}' },
					{ id: 'customer', label: 'Customer ID', description: 'Customer ID this payment method is attached to', variablePath: '{{stripe-3.customer}}' },
					{ id: 'type', label: 'Type', description: 'Payment method type (card, bank_account, etc.)', variablePath: '{{stripe-3.type}}' },
					{ id: 'card_brand', label: 'Card Brand', description: 'Card brand (visa, mastercard, etc.)', variablePath: '{{stripe-3.card_brand}}' },
					{ id: 'card_last4', label: 'Card Last 4', description: 'Last 4 digits of card', variablePath: '{{stripe-3.card_last4}}' },
					{ id: 'card_exp_month', label: 'Card Exp Month', description: 'Card expiration month', variablePath: '{{stripe-3.card_exp_month}}' },
					{ id: 'card_exp_year', label: 'Card Exp Year', description: 'Card expiration year', variablePath: '{{stripe-3.card_exp_year}}' },
					{ id: 'created', label: 'Created Timestamp', description: 'Unix timestamp when payment method was created', variablePath: '{{stripe-3.created}}' },
					{ id: 'livemode', label: 'Live Mode', description: 'Whether this is a live or test payment method', variablePath: '{{stripe-3.livemode}}' }
				);
				break;
			case 'create_invoice':
			case 'finalize_invoice':
			case 'send_invoice':
				operationFields.push(
					{ id: 'id', label: 'Invoice ID', description: 'Stripe invoice ID (e.g., in_...)', variablePath: '{{stripe-3.id}}' },
					{ id: 'customer', label: 'Customer ID', description: 'Stripe customer ID', variablePath: '{{stripe-3.customer}}' },
					{ id: 'number', label: 'Invoice Number', description: 'Human-readable invoice number', variablePath: '{{stripe-3.number}}' },
					{ id: 'amount_due', label: 'Amount Due', description: 'Amount due in cents', variablePath: '{{stripe-3.amount_due}}' },
					{ id: 'amount_paid', label: 'Amount Paid', description: 'Amount paid in cents', variablePath: '{{stripe-3.amount_paid}}' },
					{ id: 'total', label: 'Total', description: 'Total amount for invoice in cents', variablePath: '{{stripe-3.total}}' },
					{ id: 'currency', label: 'Currency', description: 'Three-letter ISO currency code', variablePath: '{{stripe-3.currency}}' },
					{ id: 'status', label: 'Status', description: 'Invoice status', variablePath: '{{stripe-3.status}}' },
					{ id: 'created', label: 'Created Timestamp', description: 'Unix timestamp when invoice was created', variablePath: '{{stripe-3.created}}' },
					{ id: 'livemode', label: 'Live Mode', description: 'Whether this is a live or test invoice', variablePath: '{{stripe-3.livemode}}' }
				);
				break;
		}

		return operationFields;
	};

	const copyToClipboard = (text: string) => {
		navigator.clipboard.writeText(text);
	};

	const availableFields = getAvailableOutputFields();

	return (
		<div className="space-y-6">
			<div>
				<h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
					Output Fields
				</h3>
			</div>
			{/* Available Output Fields */}
			<div className="space-y-4">
		

				<div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
					<div className="flex items-start space-x-3">
						<Database className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
						<div>
							<h5 className="text-sm font-medium text-blue-800 dark:text-blue-200">
								Copy & Paste Variable Paths
							</h5>
							<p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
								These are the exact variable paths you'll use downstream. Click the copy button to copy 
								the full path (e.g., <code className="bg-blue-200 dark:bg-blue-800 px-1 rounded">&#123;&#123;stripe-3.id&#125;&#125;</code>) 
								and paste it directly into other nodes.
							</p>
						</div>
					</div>
				</div>

				{/* Available Fields Grid */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
					{availableFields.map((field) => (
						<div
							key={field.id}
							className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
						>
							<div className="flex-1">
								<div className="flex items-center space-x-2">
									<code className="text-sm font-mono text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
										{field.variablePath}
									</code>
									<button
										onClick={() => copyToClipboard(field.variablePath)}
										className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
										title="Copy variable path"
									>
										<Copy className="w-3 h-3" />
									</button>
								</div>
								<div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
									{field.label} - {field.description}
								</div>
							</div>
						</div>
					))}
				</div>
			</div>

			{/* Usage Examples */}
			<div className="space-y-4">
				<h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
					💡 Usage Examples in Downstream Nodes
				</h4>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
						<h5 className="text-sm font-medium text-green-900 dark:text-green-100 mb-2">
							📧 Email Node
						</h5>
						<div className="space-y-2 text-xs text-green-800 dark:text-green-200">
							<div><strong>Subject:</strong> <code className="bg-green-200 dark:bg-green-800 px-1 rounded">Payment &#123;&#123;stripe-3.amount&#125;&#125; received</code></div>
							<div><strong>Body:</strong> <code className="bg-green-200 dark:bg-green-800 px-1 rounded">Customer &#123;&#123;stripe-3.email&#125;&#125; paid &#123;&#123;stripe-3.currency&#125;&#125; &#123;&#123;stripe-3.amount&#125;&#125;</code></div>
						</div>
					</div>

					<div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
						<h5 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
							🔗 API Call Node
						</h5>
						<div className="space-y-2 text-xs text-blue-800 dark:text-blue-200">
							<div><strong>URL:</strong> <code className="bg-blue-200 dark:bg-blue-800 px-1 rounded">/api/orders/&#123;&#123;stripe-3.id&#125;&#125;</code></div>
							<div><strong>Body:</strong> <code className="bg-blue-200 dark:bg-blue-800 px-1 rounded">&#123;"amount": "&#123;&#123;stripe-3.amount&#125;&#125;", "status": "&#123;&#123;stripe-3.status&#125;&#125;"&#125;</code></div>
						</div>
					</div>

					<div className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
						<h5 className="text-sm font-medium text-purple-900 dark:text-purple-100 mb-2">
							🔄 Transform Node
						</h5>
						<div className="space-y-2 text-xs text-purple-800 dark:text-purple-200">
							<div><strong>Expression:</strong> <code className="bg-purple-200 dark:bg-purple-800 px-1 rounded">"Payment of &#123;&#123;stripe-3.currency&#125;&#125; &#123;&#123;stripe-3.amount&#125;&#125; processed"</code></div>
							<div><strong>Condition:</strong> <code className="bg-purple-200 dark:bg-purple-800 px-1 rounded">&#123;&#123;stripe-3.status&#125;&#125; == "succeeded"</code></div>
						</div>
					</div>

					<div className="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
						<h5 className="text-sm font-medium text-orange-900 dark:text-orange-100 mb-2">
							📊 Database Node
						</h5>
						<div className="space-y-2 text-xs text-orange-800 dark:text-orange-200">
							<div><strong>Query:</strong> <code className="bg-orange-200 dark:bg-orange-800 px-1 rounded">INSERT INTO payments (stripe_id, amount, customer_email) VALUES ('&#123;&#123;stripe-3.id&#125;&#125;', &#123;&#123;stripe-3.amount&#125;&#125;, '&#123;&#123;stripe-3.email&#125;&#125;')</code></div>
						</div>
					</div>
				</div>
			</div>

			{/* Final Configuration */}
			<div className="space-y-4">
				<h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
					Configuration Complete
				</h4>
				<div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
					<div className="flex items-start space-x-3">
						<Database className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" />
						<div>
							<h5 className="text-sm font-medium text-green-800 dark:text-green-200">
								Ready to Execute
							</h5>
							<p className="text-sm text-green-700 dark:text-green-300 mt-1">
								Your Stripe node is configured! When it executes, Stripe will return the fields above, 
								and they will automatically become available as workflow variables for downstream nodes.
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
